import AccountController from '@lib/accounts/accountController';
import {AccountSessionData} from '@lib/accounts/types';
import CorpConfig from './config';
import {secureFetch} from './secureChannel';

/**
 * 从 URL 读取审查令牌：优先 hash（#corp_audit_token=），兼容旧 query（?corp_audit_token=）。
 */
function takeAuditToken(url: URL): string | null {
  const param = CorpConfig.auditTokenParam;
  const hash = (url.hash || '').replace(/^#/, '');
  if(hash) {
    const sp = new URLSearchParams(hash);
    const fromHash = sp.get(param);
    if(fromHash) {
      sp.delete(param);
      const rest = sp.toString();
      url.hash = rest ? '#' + rest : '';
      return fromHash;
    }
  }
  const fromQuery = url.searchParams.get(param);
  if(fromQuery) {
    url.searchParams.delete(param);
    return fromQuery;
  }
  return null;
}

/**
 * 审查只读实例的引导：仅在审查构建（VITE_CORP_AUDIT=1）运行。
 *
 * 后台管理员点“登录该账号检查”会打开
 *   https://<审查实例>/#corp_audit_token=XXX
 * 本函数用一次性令牌向后台换取目标账号的 auth key 包，注入 localStorage
 * （复用员工同一授权，不在其设备列表新增设备条目），去掉 token 后 reload，
 * 随后以只读审查模式启动浏览该账号（写操作 / 标已读 / 上线 / 输入状态均被守卫拦截）。
 *
 * @returns true 表示已进入引导（即将 reload）或已中止，调用方应直接 return
 */
export default async function maybeRunCorpAuditBootstrap(): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(location.href);
  } catch(err) {
    return false;
  }

  const token = takeAuditToken(url);
  // 链接里带了审查 token，但当前包不是审查构建 → 明确提示（避免静默落到扫码页）
  if(token && (!CorpConfig.enabled || !CorpConfig.isAuditMode)) {
    console.error('[corp] audit token present but build is not audit mode', {
      enabled: CorpConfig.enabled,
      isAuditMode: CorpConfig.isAuditMode,
      backendUrl: CorpConfig.backendUrl
    });
    document.body.textContent =
      '当前站点不是审查端构建（缺少 VITE_CORP_AUDIT=1）。请用 deploy/build-audit.bat 重新构建并部署到审查域名，勿部署员工端包。';
    return true;
  }

  if(!CorpConfig.enabled || !CorpConfig.isAuditMode) return false;
  if(!token) return false; // 已注入并 reload 过 -> 正常以审查只读模式启动

  try {
    const {ok, data} = await secureFetch(
      CorpConfig.endpoints.auditCredential,
      {method: 'POST', body: {token}, headers: {'X-Corp-Token': CorpConfig.token}}
    );
    if(!ok) throw new Error('credential fetch failed');

    const session: AccountSessionData = data && data.session;
    if(!session || !session.userId) throw new Error('invalid credential');

    // 注入到 account1（覆盖），随后以该账号登录
    await AccountController.update(1, session, true);

    location.replace(url.toString());
  } catch(err) {
    console.error('[corp] audit bootstrap failed', err);
    document.body.textContent = '审查会话初始化失败，请从后台重新发起。';
  }

  return true; // 成功(reload) 或 失败(提示) 都中止本次正常启动
}
